import React from 'react';
import type { DetectionHistoryItem, ContentDetectionPayload } from '@redbax/shared';
import { formatDateTime } from '@redbax/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
} from '@redbax/ui-kit';

function categoryBadgeVariant(category: string) {
  switch (category) {
    case 'PII':
      return 'info' as const;
    case 'PCI':
      return 'warning' as const;
    case 'Confidential':
      return 'destructive' as const;
    default:
      return 'secondary' as const;
  }
}

function actionBadgeVariant(action: string) {
  switch (action) {
    case 'Block':
      return 'destructive' as const;
    case 'Warn':
      return 'warning' as const;
    case 'Log':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

function tryParsePayload(raw: string): ContentDetectionPayload | null {
  try {
    return JSON.parse(raw) as ContentDetectionPayload;
  } catch {
    return null;
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

interface ContentDetectionModalProps {
  event: DetectionHistoryItem;
  open: boolean;
  onClose: () => void;
}

export function ContentDetectionModal({ event, open, onClose }: ContentDetectionModalProps) {
  const payload = tryParsePayload(event.payload);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Content Detection Details</DialogTitle>
          <DialogDescription>
            Event detected at {formatDateTime(event.occurredAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Category</p>
              <Badge variant={categoryBadgeVariant(event.category)}>{event.category}</Badge>
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Action Taken</p>
              <Badge variant={actionBadgeVariant(event.actionTaken)}>{event.actionTaken}</Badge>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Rule Name</p>
            <p className="text-sm text-text-primary">{event.ruleName}</p>
          </div>

          {payload?.confidence !== undefined && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Confidence</p>
              <p className="text-sm text-text-primary">{(payload.confidence * 100).toFixed(1)}%</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Target Application</p>
            <p className="text-sm text-text-primary">{event.targetApplication}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Hostname</p>
            <p className="text-sm text-text-primary">{event.hostname}</p>
          </div>

          {payload?.content_hash && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Content Hash</p>
              <p className="text-sm text-text-primary font-mono truncate" title={payload.content_hash}>
                {payload.content_hash}
              </p>
            </div>
          )}

          {payload?.content_length !== undefined && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-1">Content Length</p>
              <p className="text-sm text-text-primary">{formatNumber(payload.content_length)} characters</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Event ID</p>
            <p className="text-sm text-text-secondary font-mono truncate" title={event.eventId}>
              {event.eventId}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
